import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'

import { ThemeProvider } from '@mui/material'

import { getTheme } from 'themes'

import { formatUserOption, isNewUserInvite } from 'utils/functions'

/* Atoms */
import Button from 'components_new/atoms/Button'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'
import Tooltip from 'components_new/atoms/Tooltip'
import TooltipWrapper from 'components_new/atoms/TooltipWrapper'

/* Molecules */
import FormSection from 'components_new/molecules/FormSection'
import GenericListItem from 'components_new/molecules/GenericListItem'
import RichTextEditor from 'components_new/molecules/RichTextEditor'
import { RichText } from 'components_new/molecules/RichTextEditor/types'
import SelectSearch, { Option } from 'components_new/molecules/SelectSearch'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import StatusBox from 'components_new/molecules/StatusBox'

/* Organisms */
import DashboardPermissions from 'components_new/organisms/DashboardPermissions'
import PrivateKpiStatusBox from 'components_new/organisms/PrivateKpiStatusBox'

import { dict } from './lib'
import { FolderChip, isValidTitle } from './utils'

/* Actions */
import { useDashboardGroupActions } from 'redux/actions/DashboardGroups'
import * as UserEvents from 'redux/api/internal/UserEvents'

/* Hooks */
import { useAccounts } from 'redux/hooks/Accounts'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useTranslation } from 'language/translator'
import { useDashboardFolders } from 'redux/hooks/DashboardFolders'
import { useAllowEditDashboardGroup } from 'redux/hooks/Permissions'

/* Types */
import { AccountBody } from 'redux/reducers/Accounts'
import { TPostInviteToDashboardGroupPermission } from 'redux/api/DashboardGroups/types'
import {
  DashboardStatus,
  TDashboardGroup,
  WhoCanView
} from 'types/GlobalDashboardGroup'
import { Action } from 'types/GlobalUserEvents'

export type TVariant = 'edit' | 'info' | 'publish' | 'welcome'

interface AboutDashboardDialogProps {
  dashboardGroup: TDashboardGroup | null
  onClose: () => void
  open: boolean
  variant: TVariant
  setVariant: (variant: TVariant) => void
}

function formatUserOptionWithHomepal(
  accountBody: AccountBody,
  ownerId: string
): Option & { id: string } {
  if (ownerId === 'HOMEPAL') {
    return {
      avatar: 'user',
      body: '',
      id: 'HOMEPAL',
      title: 'Homepal'
    }
  }

  return formatUserOption(accountBody[ownerId] || null)
}

const AboutDashboardDialog = (props: AboutDashboardDialogProps) => {
  const { dashboardGroup, onClose, open, variant, setVariant } = props

  const { t } = useTranslation()

  const { tryUpdateDashboardGroup } = useDashboardGroupActions()

  const { account, isHomepal } = useAccountSettings()
  const { accounts, accountBody } = useAccounts()
  const folders = useDashboardFolders()
  const ownerId = dashboardGroup?.owner

  const allowEditDashboardGroup = useAllowEditDashboardGroup(dashboardGroup?.id)

  const [description, setDescription] = useState<RichText | null>(
    dashboardGroup?.description ?? null
  )
  const [owner, setOwner] = useState<Option | null>(
    formatUserOptionWithHomepal(accountBody, ownerId || '')
  )
  const [title, setTitle] = useState<string | null>(
    dashboardGroup?.title ?? null
  )
  const [folder, setFolder] = useState<string | null>(
    dashboardGroup?.folder_id || null
  )
  const [whoCanView, setWhoCanView] = useState<WhoCanView>(
    dashboardGroup?.share_settings.who_can_view ?? WhoCanView.ONLY_ADDED_USERS
  )
  const [permissions, setPermissions] = useState<
    TPostInviteToDashboardGroupPermission[]
  >(dashboardGroup?.share_settings.permissions ?? [])
  const isEdit = variant === 'edit'
  const isInfo = variant === 'info'
  const isPublish = variant === 'publish'
  const isWelcome = variant === 'welcome'

  const showEdit = allowEditDashboardGroup && (isWelcome || isInfo)
  const editOrPublish = isEdit || isPublish

  const resetFields = (dg: TDashboardGroup) => {
    setDescription(dg.description)
    setOwner(formatUserOptionWithHomepal(accountBody, dg.owner || ''))
    setTitle(dg.title)
    setFolder(dg.folder_id)
  }

  useEffect(() => {
    if (open && dashboardGroup) {
      resetFields(dashboardGroup)
    }
  }, [open, dashboardGroup])

  const userOptions: Option[] = useMemo(() => {
    return accounts
      .filter((account) => !account.company_group_id)
      .map((account) => formatUserOption(account))
  }, [accounts])

  // loading
  if (!dashboardGroup) {
    return null
  }

  const handleClose = () => {
    if (isEdit) {
      resetFields(dashboardGroup)
      setVariant('info')
    } else {
      onClose()
    }
  }

  const handleSubmit = () => {
    const ownerId =
      isPublish && isHomepal && !owner?.id
        ? 'HOMEPAL'
        : (owner?.id as string | undefined)

    if (
      isValidTitle(title) &&
      (ownerId || dashboardGroup.status === DashboardStatus.DRAFT)
    ) {
      tryUpdateDashboardGroup(
        dashboardGroup.id,
        {
          description,
          title: title as string,
          owner: ownerId,
          folder_id: folder,
          // publish specific fields
          status: isPublish ? DashboardStatus.PUBLISHED : undefined,
          permission_type: isPublish ? whoCanView : undefined,
          permissions: isPublish ? permissions : undefined
        },
        (e) => {
          if (!e) {
            if (isPublish) {
              UserEvents.log({
                action: Action.DASHBOARD_PUBLISHED,
                context: null,
                dashboard_id: null,
                dashboard_group_id: dashboardGroup.id
              })
            }

            setTimeout(() => setVariant('info'), 300)
          }
        }
      )
    }
  }

  return (
    <ThemeProvider theme={getTheme('light')}>
      <SimpleDialog
        actions={
          editOrPublish ? (
            <>
              <Button variant="text" onClick={handleClose}>
                {t('global', dict[variant].closeLabel)}
              </Button>
              {dict[variant].submitLabel ? (
                <Button
                  disabled={!isValidTitle(title)}
                  onClick={handleSubmit}
                  variant="contained"
                >
                  {t('global', dict[variant].submitLabel)}
                </Button>
              ) : null}
            </>
          ) : (
            <Button
              variant={isInfo ? 'text' : 'contained'}
              onClick={handleClose}
            >
              {t('global', dict[variant].closeLabel)}
            </Button>
          )
        }
        contentHero={
          isWelcome
            ? {
                body: (
                  <>
                    {t('dashboards', 'intro_text_part_1')}{' '}
                    {dashboardGroup.title}{' '}
                    {t('dashboards', 'intro_text_part_2')}
                  </>
                ),
                title: `${t('global', 'welcome')}, ${account?.first_name} 👋`
              }
            : undefined
        }
        disableRestoreFocus={true}
        dividers={editOrPublish}
        onClose={handleClose}
        open={open}
        title={
          editOrPublish
            ? t(
              'dashboards',
              isPublish ? 'publish_dashboard' : 'edit_information'
            )
            : undefined
        }
      >
        {editOrPublish ? (
          <StatusBox severity="info" sx={{ mb: 2 }}>
            {t('dashboards', 'initial_info_note')}
          </StatusBox>
        ) : null}
        {editOrPublish ? (
          <FormSection
            actions={
              showEdit ? (
                <Button
                  onClick={() => setVariant('edit')}
                  size="small"
                  variant="text"
                >
                  {t('global', 'edit')}
                </Button>
              ) : null
            }
            helperText={t('dashboards', 'name_helper_text')}
            importance={'required'}
            title={t('global', 'name')}
          >
            <TextField
              autoComplete="off"
              autoFocus={editOrPublish}
              error={!isValidTitle(title)}
              fullWidth
              helperText={
                !isValidTitle(title)
                  ? t('dashboards', 'name_error_text')
                  : undefined
              }
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                setTitle(event.target.value)
              }}
              placeholder={t('dashboards', 'name_placeholder')}
              required={true}
              value={title}
            />
          </FormSection>
        ) : null}

        {variant === 'publish' ? (
          <FormSection
            helperText={t('dashboards', 'access_helper_text')}
            importance={'required'}
            title={t('global', 'access')}
          >
            <PrivateKpiStatusBox id={dashboardGroup.id} />
            <DashboardPermissions
              dashboardGroup={dashboardGroup}
              permissions={permissions}
              onInvite={(newPermission) => {
                setPermissions((prev) => [...prev, newPermission])
              }}
              revokeAccess={(revoked) =>
                setPermissions((prev) =>
                  prev.filter((perm) => {
                    if (isNewUserInvite(perm)) {
                      return true
                    }

                    if (revoked.account_id) {
                      return perm.account_id !== revoked.account_id
                    } else if (revoked.group_id) {
                      return perm.group_id !== revoked.group_id
                    }

                    return perm.company_group_id !== revoked.company_group_id
                  })
                )
              }
              setWhoCanView={setWhoCanView}
              whoCanView={whoCanView}
            />
          </FormSection>
        ) : null}

        <FormSection
          actions={
            showEdit ? (
              <Button
                onClick={() => setVariant('edit')}
                size="small"
                variant="text"
              >
                {t('global', 'edit')}
              </Button>
            ) : null
          }
          helperText={
            editOrPublish
              ? t('dashboards', 'about_dashboard_helper_text')
              : null
          }
          importance={editOrPublish ? 'recommended' : undefined}
          title={t('dashboards', 'about_dashboard')}
        >
          {(isInfo || isWelcome) && !description ? (
            <Text color="text.disabled">
              {t('dashboards', 'about_dashboard_missing')}
            </Text>
          ) : (
            <RichTextEditor
              edit={editOrPublish}
              noGutter={true}
              onChange={(value) => setDescription(value)}
              placeholder={t('dashboards', 'about_dashboard_placeholder')}
              value={description}
              sx={!isEdit ? { mt: 2 } : undefined}
              variant="outlined"
            />
          )}
        </FormSection>

        {!isWelcome ? (
          <FormSection
            actions={
              showEdit ? (
                <Button
                  onClick={() => setVariant('edit')}
                  size="small"
                  variant="text"
                >
                  {t('global', 'edit')}
                </Button>
              ) : null
            }
            helperText={
              editOrPublish
                ? t('dashboards', 'placement_helper_text')
                : undefined
            }
            importance={editOrPublish ? 'recommended' : undefined}
            title={t('dashboards', 'placement_title')}
          >
            {editOrPublish ? (
              <Tooltip
                title={
                  !isPublish && dashboardGroup.status === DashboardStatus.DRAFT
                    ? t(
                      'dashboards',
                      'folder_selection_requires_publish_tooltip'
                    )
                    : undefined
                }
              >
                <TextField
                  fullWidth={true}
                  label={`${t('dashboards', 'select_folder')}...`}
                  select={true}
                  autoComplete="off"
                  value={folder}
                  onChange={(event) => setFolder(event.target.value)}
                  sx={{ mt: 1, mb: 2 }}
                  disabled={
                    !isPublish &&
                    dashboardGroup.status === DashboardStatus.DRAFT
                  }
                >
                  <MenuItem value={null}>
                    <em>{t('global', 'none')}</em>
                  </MenuItem>
                  {folders.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Tooltip>
            ) : (
              <FolderChip
                folder={folders.find((opt) => opt.id === folder) || null}
                status={dashboardGroup.status}
              />
            )}
          </FormSection>
        ) : null}

        <FormSection
          actions={
            showEdit ? (
              <Button
                onClick={() => setVariant('edit')}
                size="small"
                variant="text"
              >
                {t('global', 'edit')}
              </Button>
            ) : null
          }
          helperText={
            editOrPublish ? t('dashboards', 'owner_helper_text') : undefined
          }
          importance={editOrPublish ? 'required' : undefined}
          title={t('global', 'owner')}
        >
          {editOrPublish ? (
            <TooltipWrapper
              title={
                !isPublish && dashboardGroup.status === DashboardStatus.DRAFT
                  ? t('dashboards', 'owner_change_requires_publish_tooltip')
                  : undefined
              }
            >
              <SelectSearch
                color="primary"
                disableClearable={true}
                disabled={
                  !isPublish && dashboardGroup.status === DashboardStatus.DRAFT
                }
                onChange={setOwner}
                multiple={false}
                options={userOptions}
                selected={owner}
                size={'medium'}
                sort={true}
              />
            </TooltipWrapper>
          ) : (
            <GenericListItem
              title={owner?.title ?? 'Ingen ägare'}
              body={owner?.body}
              variant={owner?.title ? undefined : 'role'}
            />
          )}
        </FormSection>
      </SimpleDialog>
    </ThemeProvider>
  )
}

export default AboutDashboardDialog
